import React, { useState, useEffect, useCallback, useMemo } from 'react'; // 添加useMemo优化
import {
  Box,
  Typography,
  Button,
  styled,
  useTheme
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import QuestionSplit from './components/question_split';
import LeftCardView from './components/LeftCardView';
import RightReadingView from './components/RightReadingView';
import CharacterIntro from './components/CharacterIntro';
import FeedbackView from './components/FeedbackView';
import RightQuestion from './components/RightQuestion';

import { saveDetailNPCReading, getCurrentUser } from './services/userReadingService';
import { languages, useLanguage, navigateWithLang, translateFramework } from './data/languages.js';

const DetailView = styled(Box)(({ theme }) => ({
  position: 'fixed',
  height: '100vh',
  width: '100vw',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
}));

const LeftSection = styled(Box)(({ theme }) => ({
  width: '50%',
  height: '100vh',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  overflow: 'auto',
}));

const RightSection = styled(Box)(({ theme }) => ({
  width: '50%',
  backgroundColor: 'rgba(255, 255, 255)',
  // padding: theme.spacing(8),
  height: '100vh',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
}));

const LoadingView = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
}));

const RedirectOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
}));

const AnimatedBackground = styled(Box)(({ colors }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundImage: colors ? 
    colors.map((color, index) => 
      `radial-gradient(closest-side, ${color}, ${color}00)`
    ).join(',') :
    `
      radial-gradient(closest-side, #ece5d4, #ece5d400),
      radial-gradient(closest-side, #e6cda8, #e6cda800),
      radial-gradient(closest-side, #8dc1c9, #8dc1c900)
    `,
  backgroundSize: colors ?
    colors.map(() => '130vmax 130vmax').join(',') :
    '130vmax 130vmax, 130vmax 130vmax, 130vmax 130vmax',
  backgroundPosition: colors ?
    colors.map((_, index) => `${-30 + (index * 20)}vmax ${-30 + (index * 20)}vmax`).join(',') :
    '-60vmax -60vmax, 0vmax 0vmax, 60vmax 60vmax',
  backgroundRepeat: 'no-repeat',
  animation: 'movement 20s linear infinite',
  zIndex: -1,
  transition: 'all 0.5s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  '@keyframes movement': {
    '0%, 100%': {
      backgroundSize: '130vmax 130vmax, 80vmax 80vmax, 90vmax 90vmax, 110vmax 110vmax, 90vmax 90vmax',
      backgroundPosition: '-80vmax -80vmax, 60vmax -30vmax, 10vmax 10vmax, -30vmax -10vmax, 50vmax 50vmax',
    },
    '25%': {
      backgroundSize: '100vmax 100vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 60vmax 60vmax',
      backgroundPosition: '-60vmax -90vmax, 50vmax -40vmax, 0vmax -20vmax, -40vmax -20vmax, 40vmax 60vmax',
    },
    '50%': {
      backgroundSize: '80vmax 80vmax, 110vmax 110vmax, 80vmax 80vmax, 60vmax 60vmax, 80vmax 80vmax',
      backgroundPosition: '-50vmax -70vmax, 40vmax -30vmax, 10vmax 0vmax, 20vmax 10vmax, 30vmax 70vmax',
    },
    '75%': {
      backgroundSize: '90vmax 90vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 70vmax 70vmax',
      backgroundPosition: '-50vmax -40vmax, 50vmax -30vmax, 20vmax 0vmax, -10vmax 10vmax, 40vmax 60vmax',
    },
  },
}));

export default function DetailNPC({ onSelectNPC, onCardDraw }) {
  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get('language') || 'en'; // URL语言参数
  const currentLang = useLanguage() || urlLang; // 响应式语言配置，URL优先
  const config = useMemo(() => languages[currentLang], [currentLang]); // 优化语言配置获取
  const [viewState, setViewState] = useState('question');
  const [cardData, setCardData] = useState(null);
  const [cardImages, setCardImages] = useState({});
  const [answer, setAnswer] = useState('');
  const [cardIndex, setCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrollingToIndex, setIsScrollingToIndex] = useState(false);
  const [history, setHistory] = useState([]);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpAnswers, setFollowUpAnswers] = useState([]);
  const [currentView, setCurrentView] = useState('reading');
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [initialStressLevel, setInitialStressLevel] = useState(null);
  const [finalStressLevel, setFinalStressLevel] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const loadingMessages = config.detailNPC.loadingMessages;

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { character, readingData, fromHistory } = location.state || {};

  // Calculate total sections (cards + summary)
  const totalSections = (cardData?.cards?.split(', ').length + 1) || 1;
  console.log("totalSections", totalSections);

  // Get reading data if coming from history
  const { readingData: historyReadingData, fromHistory: fromHistoryReading } = location.state || {};
  
  // Initialize state with reading data if available
  useEffect(() => {
    if (fromHistoryReading && historyReadingData) {
      setCardData({
        cards: historyReadingData.cards,
        selectedQuestion: historyReadingData.question,
        framework: historyReadingData.framework,
      });
      setAnswer(historyReadingData.answer);
      setHistory(historyReadingData.history || []);
      setInitialStressLevel(historyReadingData.initialStressLevel);
      setViewState('reading');
    }
  }, [fromHistoryReading, historyReadingData]);

  useEffect(() => {
    if (cardData) {
      loadCardImages();
    }
  }, [cardData]);

  const loadCardImages = async () => {
    if (!cardData?.cards) return; // 检查 cardData.cards 是否存在

    const newImages = {
      'Card Back': '/card_back.jpg'
    };
    
    const cards = cardData.cards.split(', ');
    for (const card of cards) {
      try {
        const response = await fetch(`/cards/${card}`);
        if (response.ok) {
          const blob = await response.blob();
          newImages[card] = URL.createObjectURL(blob);
        }
      } catch (error) {
        console.error('Error loading card image:', error);
      }
    }

    setCardImages(newImages);
  };

  const handleInitialStressChange = (level) => {
    setInitialStressLevel(level);
    // console.log(`Initial happiness level set to: ${level}/5`);
  };

  const handleFinalStressChange = (rating) => {
    setFinalStressLevel(rating);
    // console.log(`Final happiness level set to: ${rating}/5`);
  };

  const handleCardDraw = async (data) => {
    // Generate a unique ID for this reading that will be used for both initial save and updates
    const readingId = `reading_${Date.now()}`;
    
    setCardData({
      ...data,
      id: readingId, // Store the ID in cardData
      initialStressLevel: initialStressLevel
    });
    setIsLoading(true);
    setLoadingMessageIndex(0); // 重置加载消息索引
    setLoadingStartTime(Date.now()); // 记录开始时间

    console.log("character_id", character?.id);
    console.log("framework", data.framework);
    
    try {
      const user = getCurrentUser();
      console.log("Calling /answer endpoint with params:", {
        character: character?.id,
        framework: data.framework,
        question: data.selectedQuestion,
        cardone: data.cardOne,
        cardtwo: data.cardTwo,
        cardthree: data.cardThree,
        language: currentLang,
        user_id: user?.id || ''
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch('/answer?' + new URLSearchParams({
        character: character?.id,
        framework: data.framework,
        question: data.selectedQuestion,
        cardone: data.cardOne,
        cardtwo: data.cardTwo,
        cardthree: data.cardThree,
        language: currentLang, // 添加语言参数
        user_id: user?.id || '', // 添加用户ID参数
      }), {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log("Answer response status:", response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Answer response data:", responseData);

        setAnswer(responseData.answer);
        setHistory(responseData.history || []);
        setFollowUpHistory([]);
          
        // Save reading with complete data including initial stress level and consistent ID
        saveDetailNPCReading({
          id: readingId, // Use the same ID
          cards: data.cards,
          question: data.selectedQuestion,
          framework: data.framework[0],
          answer: responseData.answer,
          initialStressLevel: initialStressLevel,
          finalStressLevel: null, // Set to null initially
          character: character?.id || null,
          history: responseData.history || []
        });
        
        setViewState('reading');
        setCardIndex(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleContinueAsk to handle the new history format
  const handleContinueAsk = (followUpQuestion, newFollowUpHistory) => {
    setIsLoading(true);
    
    // Update the follow-up history state with the new history
    setFollowUpHistory(newFollowUpHistory);
    
    // Add the new follow-up to the list if needed for other views
    const lastAnswer = newFollowUpHistory[newFollowUpHistory.length - 1]?.content;
    if (lastAnswer) {
      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer: lastAnswer
      }]);
    }
    
    // Keep the view state as 'followup' to stay in RightQuestion component
    setViewState('followup');
    setCurrentView('followup');
    
    setIsLoading(false);
    
    // Save the full history (context + follow-up) for persistence
    const fullHistory = [...history, ...newFollowUpHistory];
    if (fromHistory && readingData) {
      const updatedReading = {
        ...readingData,
        history: fullHistory,
      };
      saveDetailNPCReading(updatedReading);
    }
  };

  const handleScroll = useCallback(({ direction }) => {
    if (isScrollingToIndex) return;
    
    const newIndex = cardIndex + direction;
    if (newIndex >= 0 && newIndex < totalSections) {
      setCardIndex(newIndex);
      setIsScrollingToIndex(true);

      console.log(cardIndex);
      
      // Reset scrolling flag after animation
      setTimeout(() => {
        setIsScrollingToIndex(false);
      }, 500);
    }
  }, [cardIndex, totalSections, isScrollingToIndex]);

  const handleCardClick = (index) => {
    const cardCount = cardData?.cards?.split(', ').length || 0;
    
    // Only allow clicking actual card indices
    if (index >= 0 && index < cardCount) {
      setCardIndex(index);
      setCurrentView('reading');
      
      // Reset any follow-up or feedback views when clicking cards
      setIsScrollingToIndex(false);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    
    // If switching to reading view, reset to the appropriate card index
    if (view === 'reading') {
      // If we were on a follow-up, go to the summary
      const cardCount = cardData?.cards?.split(', ').length || 0;
      setCardIndex(cardCount);
    } else if (view === 'followup' && followUpAnswers.length > 0) {
      // If switching to follow-up view, show the first follow-up
      const cardCount = cardData?.cards?.split(', ').length || 0;
      setCardIndex(cardCount + 1);
    }
  };

  // Add direct navigation functions for the top buttons
  const handleJumpToSummary = () => {
    const cardCount = cardData?.cards?.split(', ').length || 0;
    setCardIndex(cardCount); // Set to summary index
    setCurrentView('reading');
    setViewState('reading');
  };

  const handleJumpToFollowUp = () => {
    setCurrentView('followup');
    setViewState('followup');
  };

  // Add a function to handle scrolling between follow-up questions
  const handleFollowUpScroll = ({ direction }) => {
    if (isScrollingToIndex) return;
    
    const cardCount = cardData?.cards?.split(', ').length || 0;
    const currentFollowUpIndex = cardIndex - (cardCount + 1);
    const newFollowUpIndex = currentFollowUpIndex + direction;
    
    // Check if the new index is within bounds
    if (newFollowUpIndex >= 0 && newFollowUpIndex < followUpAnswers.length) {
      const newCardIndex = cardCount + 1 + newFollowUpIndex;
      setCardIndex(newCardIndex);
      setIsScrollingToIndex(true);
      
      // Reset scrolling flag after animation
      setTimeout(() => {
        setIsScrollingToIndex(false);
      }, 500);
    }
  };

  const handleFeedbackChange = (rating) => {
    setFeedbackRating(rating);
    setFinalStressLevel(rating);
    
    if (cardData) {
      // Update the cardData with final stress level
      const updatedCardData = {
        ...cardData,
        finalStressLevel: rating
      };
      setCardData(updatedCardData);

      // Update the existing reading with the final stress level using the same ID
      saveDetailNPCReading({
        id: cardData.id, // Use the ID stored in cardData
        cards: cardData.cards,
        question: cardData.selectedQuestion,
        framework: cardData.framework[0],
        answer: answer,
        initialStressLevel: initialStressLevel,
        finalStressLevel: rating,
        character: character?.id || null,
        history: history
      });
    }
    
    // Show redirect screen and trigger confetti
    setShowRedirect(true);
    
    // Load confetti if not already loaded
    if (!window.confetti) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      script.async = true;
      document.body.appendChild(script);
    }

    // Start confetti effect
    const confettiInterval = setInterval(() => {
      if (window.confetti) {
        const colors = rating >= 4 ? ['#4caf50', '#ff9800', '#1976d2'] : ['#1976d2', '#607d8b'];
        window.confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.7 },
          colors: colors,
          disableForReducedMotion: true
        });
      }
    }, 1000);

    // Clear interval and navigate after 3 seconds
    setTimeout(() => {
      clearInterval(confettiInterval);
      navigateWithLang(navigate, '/readings');
    }, 3000);
  };

  const handleAskFollowUp = () => {
    setViewState('followup');
    setCurrentView('followup');
  };

  // Add effect to cycle through loading messages
  useEffect(() => {
    if (isLoading) {
      setLoadingMessageIndex(0);

      // 每条消息显示3秒，循环显示
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 3000);

      return () => {
        clearInterval(interval);
        setLoadingMessageIndex(0);
      };
    }
  }, [isLoading, loadingMessages.length]);

  const renderLeftSection = () => {
    return (
      <Box sx={{ 
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {(viewState === 'reading' || viewState === 'followup') && (
          <AnimatedBackground colors={character?.colors} />
        )}
        
        {viewState === 'question' && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${character?.background})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }} />
        )}

        {(viewState === 'reading' || viewState === 'followup') && cardData && answer && !isLoading && (
          <LeftCardView 
            cardData={cardData}
            cardImages={cardImages}
            onCardClick={handleCardClick}
            index={cardIndex}
            character={character}
            onContinueAsk={handleContinueAsk}
            followUpCount={followUpAnswers.length}
            onViewChange={handleViewChange}
            currentView={currentView}
            onJumpToSummary={handleJumpToSummary}
            onJumpToFollowUp={handleJumpToFollowUp}
          />
        )}
      </Box>
    );
  };

  const renderRightSection = () => {
    if (isLoading) {
      return (
        <LoadingView>
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              textAlign: 'center',
              maxWidth: '600px',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.8)',
              letterSpacing: '0.5px',
              padding: '0 24px'
            }}
          >
            {loadingMessages[loadingMessageIndex]}
          </Typography>
        </LoadingView>
      );
    }

    switch (viewState) {
      case 'question':
        return (
          <QuestionSplit 
            selectedNPC={character}
            onCardDraw={handleCardDraw}
            onInitialChange={handleInitialStressChange}
            language={currentLang}
          />
        );
      case 'reading':
        const sections = answer?.split('\n\n') || [];
        const cardCount = cardData?.cards?.split(', ').length || 0;

        
        // Handle framework as either string or array
        const frameworkValue = Array.isArray(cardData?.framework)
          ? cardData.framework[0]
          : cardData?.framework;

        let translatedFramework = translateFramework(frameworkValue, currentLang);
        let frameworkParts = translatedFramework?.split("-");
        console.log("frameworkParts", frameworkParts, "frameworkValue:", frameworkValue);

        let cardTitle = frameworkParts && frameworkParts[cardIndex] ? frameworkParts[cardIndex] : `Card ${cardIndex + 1}`;
  
        
        // Ensure cardIndex stays within valid range
        if (cardIndex >= 0 && cardIndex < cardCount) {
          const content = sections[cardIndex];
          return (
            <RightReadingView 
              index={cardIndex}
              content={content}
              // title={`Card ${cardIndex + 1} Interpretation`}
              title={cardTitle}
              onScroll={handleScroll}
              history={history}
              isFollowUp={false}
              question={cardData?.selectedQuestion}
              totalItems={totalSections} // Updated to use totalSections
              onAskFollowUp={handleAskFollowUp}
            />
          );
        } else if (cardIndex === cardCount) {
          // Summary view
          return (
            <RightReadingView 
              index={cardCount}
              content={sections[sections.length - 1]}
              title={config.detailNPC.readingSummary}
              onScroll={handleScroll}
              history={history}
              isFollowUp={false}
              question={cardData?.selectedQuestion}
              totalItems={totalSections} // Updated to use totalSections
              isSummary={true}
              onAskFollowUp={handleAskFollowUp}
              initialStressLevel={initialStressLevel}
              currentStressLevel={finalStressLevel}
              onFeedbackChange={handleFeedbackChange}
            />
          );
        }
        break;
      case 'followup':
        return (
          <RightQuestion
            selectedNPC={character}
            onQuestionSubmit={handleContinueAsk}
            context={history}
            history={followUpHistory}
            initialStressLevel={initialStressLevel}
            finalStressLevel={finalStressLevel}
            onFinalStressChange={handleFeedbackChange}
          />
        );
      default:
        return null;
    }
  };

  if (!character && !fromHistory) {
    navigateWithLang(navigate, '/character');
    return null;
  }

  return (
    <DetailView>
      {showRedirect && (
        <RedirectOverlay>
          <Typography 
            variant="h4" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.87)',
              fontFamily: "'Space Grotesk', sans-serif",
              textAlign: 'center',
              mb: 4,
              fontWeight: 500,
            }}
          >
            {(() => {
              if (finalStressLevel > initialStressLevel) {
                return config.detailNPC.redirectMessages.improved;
              } else if (finalStressLevel === initialStressLevel) {
                return config.detailNPC.redirectMessages.same;
              } else {
                return config.detailNPC.redirectMessages.worse;
              }
            })()}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.6)',
              fontFamily: "'Space Grotesk', sans-serif",
              textAlign: 'center',
              mb: 2,
              maxWidth: '600px',
            }}
          >
            {(() => {
              if (finalStressLevel > initialStressLevel) {
                return config.detailNPC.redirectMessages.improvedText;
              } else if (finalStressLevel === initialStressLevel) {
                return config.detailNPC.redirectMessages.sameText;
              } else {
                return config.detailNPC.redirectMessages.worseText;
              }
            })()}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(0, 0, 0, 0.6)',
              fontFamily: "'Space Grotesk', sans-serif",
              position: 'absolute',
              bottom: 40,
            }}
          >
            {config.detailNPC.redirectMessages.redirectText}
          </Typography>
        </RedirectOverlay>
      )}
      <LeftSection>
        {renderLeftSection()}
      </LeftSection>
      <RightSection>
        {renderRightSection()}
      </RightSection>
    </DetailView>
  );
}